import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { createTeam } from 'store/team/actions'

import { User } from 'types/app'

import {
  activateUser,
  approveOrRejectOrganizerRequest,
  followUser,
  getAllRequests,
  getUserBalance,
  getUserInfo,
  requestToBecomeOrganizer,
  signInUser,
  signUpUser,
  updateUser,
  updateUserAccountAddress,
  getCurrentUser,
} from './actions'
import { State } from './types'

const initialState: State = {
  isLoading: false,
  isLoggedIn: false,
  isLoadingForGetUserWithName: true,
  user: null,
  error: null,
  requests: [],
  message: '',
  unreadNotifications: 0,
  userWithName: null,
  userbalance: 0,
  restrictionCode: null,
  restrictedFor: null,
}

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    signOut(state) {
      state.isLoading = false
      state.isLoggedIn = false
      state.user = null
      state.error = null

      // Preserve Privy session keys and tournament redirect URLs before clearing
      const privyKeys: { [key: string]: string } = {}
      const tournamentReturnUrl = localStorage.getItem('privy_return_url')
      const loginFromTournament = localStorage.getItem('login_from_tournament')

      // Save all Privy session keys (they start with 'privy:')
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key && key.startsWith('privy:')) {
          privyKeys[key] = localStorage.getItem(key) || ''
        }
      }

      // Clear ALL stored data
      localStorage.clear()

      // Restore Privy session keys so Privy SDK can work properly
      Object.keys(privyKeys).forEach(key => {
        localStorage.setItem(key, privyKeys[key])
      })

      // Restore tournament redirect URLs so user returns to tournament after re-login
      if (tournamentReturnUrl) {
        localStorage.setItem('privy_return_url', tournamentReturnUrl)
      }
      if (loginFromTournament) {
        localStorage.setItem('login_from_tournament', loginFromTournament)
      }
    },
    updateUserState(state, action: PayloadAction<Partial<User>>) {
      if (state.user) {
        state.user = { ...state.user, ...action.payload }
      } else {
        // Handle the case where state.user is null or undefined.
        // For example, you might want to initialize it with default values
        // or use action.payload as a full user object (if it's guaranteed to be complete).
      }
    },
    /** Region Teams: PUT /me/location succeeded — keep the cooldown date with it. */
    setUserLocation(
      state,
      action: PayloadAction<{ location: User['location']; locationNextChangeAt?: string | null }>,
    ) {
      if (state.user) {
        state.user.location = action.payload.location
        state.user.locationNextChangeAt = action.payload.locationNextChangeAt ?? null
      }
    },
    updateTeamId(state, action) {
      if (state.user) {
        state.user.teamId = action.payload
      } else {
        // Optionally handle the unexpected case where user is null
        // This could be a no-op, or you could log an error, etc.
      }
    }, // This was previously misplaced
    messageCleanUp(state) {
      state.message = ''
    },
    userWithNameCleanUp(state) {
      state.userWithName = null
      state.restrictionCode = null
      state.restrictedFor = null
    },
    updateUserWithName(
      state,
      action: PayloadAction<{ newsId: string; comment: string }>,
    ) {
      const { newsId, comment } = action.payload

      if (state.userWithName) {
        state.userWithName = {
          ...state.userWithName,
          news: state.userWithName.news.map((newsItem: any) => {
            if (newsItem.id === newsId) {
              return {
                ...newsItem,
                comments: [...newsItem.comments, comment],
              }
            }
            return newsItem
          }),
        }
      }
    },

    removeUserWithNameNews(state, action: PayloadAction<{ newsId: string }>) {
      const { newsId } = action.payload
      console.log('newsId', newsId)

      if (state.userWithName) {
        state.userWithName = {
          ...state.userWithName,
          news: state.userWithName.news.filter(
            (newsItem: any) => newsItem._id !== newsId,
          ),
        }
      }
    },

    // updateUserWithNameNews(state, action: PayloadAction<any>) {
    //   const updatedNews = action.payload

    //   if (state.userWithName) {
    //     const updatedUserWithName = {
    //       ...state.userWithName,
    //       news: {
    //         ...state.userWithName.news,
    //         ...updatedNews,
    //       },
    //     }

    //     state.userWithName = updatedUserWithName
    //   }
    // },
    updateUserWithNameNews(state, action: PayloadAction<any>) {
      const updatedNews = action.payload
      console.log('updatedNews', updatedNews)

      if (state.userWithName) {
        state.userWithName.news = state.userWithName.news.map(
          (newsItem: any) => {
            if (newsItem._id === updatedNews._id) {
              return {
                ...newsItem,
                ...updatedNews,
              }
            }
            return newsItem
          },
        )
      }
    },

    userbalanceCleanUp(state) {
      state.userbalance = 0
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(createTeam.fulfilled, (state, action) => {
        if (action.payload.teamId && state.user) {
          state.user.teamId = action.payload.teamId
        }
      })
      .addCase(signInUser.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(signInUser.fulfilled, (state, { payload }) => {
        // Payload is now just the user object - no token
        state.isLoading = false
        state.isLoggedIn = true
        // `teams` is only attached by GET /me — the /auth/v2 payload doesn't carry
        // it, so replacing the user wholesale wiped the team list until the next
        // /me fetch (tournament banners intermittently flipped to "Team Required").
        state.user = { ...payload, teams: (payload as any)?.teams ?? (state.user as any)?.teams }
        state.error = null // Clear any previous errors
        state.message = '' // Clear any previous messages
        // Privy manages tokens - we never store them in Redux
      })
      .addCase(signInUser.rejected, (state, action) => {
        state.isLoading = false
        if (action.payload) {
          state.error = action.payload.errors
        } else {
          state.error = action.error.message
        }
      })
      .addCase(signUpUser.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(signUpUser.fulfilled, (state, { payload }) => {
        state.isLoading = false
        state.user = payload
      })
      .addCase(signUpUser.rejected, (state, action) => {
        state.isLoading = false
        if (action.payload) {
          state.error = action.payload.errors || action.payload.message
        } else {
          state.error = action.error.message
        }
      })
      .addCase(activateUser.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(activateUser.fulfilled, (state, { payload }) => {
        // DEPRECATED: Old activation system - payload is just User now
        state.isLoading = false
        state.isLoggedIn = true
        state.user = payload
        // Privy manages tokens - never stored in Redux
      })
      .addCase(activateUser.rejected, (state, action) => {
        state.isLoading = false
        if (action.payload) {
          state.error = action.payload.errors
        } else {
          state.error = action.error.message
        }
      })
      .addCase(updateUser.pending, (state) => {
        state.isLoading = true
        state.error = null
        state.message = '' // Clear previous messages
      })
      .addCase(updateUser.fulfilled, (state, { payload }: any) => {
        const { message, ...user } = payload  // No token - Privy manages tokens
        console.log('updateUser.fulfilled - user:', user)
        console.log('updateUser.fulfilled - message:', message)
        state.isLoading = false

        // Update the current user state (preserve /me-attached `teams` — the
        // /me/update payload doesn't include it)
        state.user = { ...user, teams: user?.teams ?? (state.user as any)?.teams }

        // Also update userWithName if it exists (for profile page updates)
        if (state.userWithName) {
          state.userWithName = {
            ...state.userWithName,
            user: user,
          }
        }

        state.message = message
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.isLoading = false
        console.log('❌ updateUser.rejected - action:', action)

        // Handle different error payload formats
        if (action.payload && typeof action.payload === 'object') {
          // Payload is an object with message/errors
          const errorMsg = (action.payload as any).message || (action.payload as any).errors || 'Update failed'
          state.error = errorMsg
          state.message = errorMsg
        } else if (typeof action.payload === 'string') {
          // Payload is a string
          state.error = action.payload
          state.message = action.payload
        } else if (action.error) {
          // Fall back to error.message
          state.error = action.error.message || 'Update failed'
          state.message = action.error.message || 'Update failed'
        } else {
          // Unknown error format
          state.error = 'An unknown error occurred'
          state.message = 'An unknown error occurred'
        }
      })
      .addCase(requestToBecomeOrganizer.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(requestToBecomeOrganizer.fulfilled, (state) => {
        state.isLoading = false
        state.message = 'Request sent successfully'
      })
      .addCase(requestToBecomeOrganizer.rejected, (state, action) => {
        state.isLoading = false
        // console.log('action in error', action)

        if (action.payload) {
          state.error = action.payload.errors
          state.message = action.payload.message
        } else {
          state.error = action.error.message
        }
      })
      .addCase(getAllRequests.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(getAllRequests.fulfilled, (state, { payload }) => {
        state.isLoading = false
        state.requests = payload
      })
      .addCase(getAllRequests.rejected, (state, action) => {
        state.isLoading = false
        if (action.payload) {
          state.error = action.payload.errors
        } else {
          state.error = action.error.message
        }
      })
      .addCase(approveOrRejectOrganizerRequest.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(
        approveOrRejectOrganizerRequest.fulfilled,
        (state, { payload }) => {
          state.isLoading = false

          // Extract the updated request status and _id from the payload
          const { _id, status }: any = payload.request

          const requestToUpdate = state.requests.find(
            (request: any) => request.id === _id,
          )

          if (requestToUpdate) {
            requestToUpdate.status = status
          }

          state.message = payload.message
          state.unreadNotifications = payload.unreadNotifications
        },
      )

      // .addCase(
      //   approveOrRejectOrganizerRequest.fulfilled,
      //   (state, { payload }) => {
      //     state.isLoading = false
      //     // console.log(payload, 'payload')
      //     // console.log(state.requests, 'state.requests')

      //     // Extract the updated request status from the payload
      //     const { _id, status }: any = payload.request
      //     // console.log(status, 'status')

      //     // Update the status of the request with the matching _id
      //     state.requests = state.requests.map((request: any) => {
      //       if (request._id === _id) {
      //         // Return a new object with the updated status
      //         return { ...request, status }
      //       }
      //       return request
      //     })
      //     state.message = payload.message
      //   },
      // )

      .addCase(approveOrRejectOrganizerRequest.rejected, (state, action) => {
        state.isLoading = false
        if (action.payload) {
          state.error = action.payload.errors
        } else {
          state.error = action.error.message
        }
      })
      .addCase(followUser.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(followUser.fulfilled, (state, { payload }) => {
        state.isLoading = false
        console.log('payload', payload)

        // Update the current user's followings from the response
        if (state.user && payload.user) {
          state.user.followings = payload.user.followings || []
        }

        // Also update userWithName.user.followers if viewing that profile
        if (state.userWithName) {
          const targetUserId = state.userWithName.user._id
          const isNowFollowing = payload.user?.followings?.includes(targetUserId)

          if (isNowFollowing && !state.userWithName.user.followers.includes(payload.user._id)) {
            state.userWithName.user.followers.push(payload.user._id)
          } else if (!isNowFollowing) {
            state.userWithName.user.followers =
              state.userWithName.user.followers.filter(
                (follower: any) => follower !== payload.user._id,
              )
          }
        }

        state.message = payload.message
      })
      .addCase(followUser.rejected, (state, action) => {
        state.isLoading = false
        if (action.payload) {
          state.error = action.payload.errors
        } else {
          state.error = action.error.message
        }
      })
      .addCase(getUserInfo.pending, (state) => {
        state.isLoadingForGetUserWithName = true
        state.error = null
        state.restrictionCode = null
        state.restrictedFor = null
      })
      .addCase(getUserInfo.fulfilled, (state, { payload }) => {
        console.log('payload user info', payload)

        state.userWithName = payload
        state.isLoadingForGetUserWithName = false
        state.restrictionCode = null
        state.restrictedFor = null
      })
      .addCase(getUserInfo.rejected, (state, action) => {
        state.isLoadingForGetUserWithName = false
        // Capture the moderation-privacy code (e.g. PROFILE_RESTRICTED) + the username it
        // applies to so the view can render a "log in to view" gate without flashing it on
        // the next profile during in-place navigation.
        state.restrictionCode = (action.payload as any)?.code ?? null
        state.restrictedFor = (action.meta?.arg as string) ?? null
        if (action.payload) {
          state.error = action.payload.errors
        } else {
          state.error = action.error.message
        }
      })
      .addCase(updateUserAccountAddress.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(updateUserAccountAddress.fulfilled, (state, { payload }) => {
        state.isLoading = false
        // Preserve /me-attached `teams` — this payload doesn't include it
        state.user = { ...payload, teams: (payload as any)?.teams ?? (state.user as any)?.teams }
      })
      .addCase(updateUserAccountAddress.rejected, (state, action) => {
        state.isLoading = false
        if (action.payload) {
          state.error = action.payload.errors
        } else {
          state.error = action.error.message
        }
      })
      .addCase(getUserBalance.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(getUserBalance.fulfilled, (state, { payload }) => {
        state.isLoading = false
        state.userbalance = payload.balance
      })
      .addCase(getUserBalance.rejected, (state, action) => {
        state.isLoading = false
        if (action.payload) {
          state.error = action.payload.errors
        } else {
          state.error = action.error.message
        }
      })
      .addCase(getCurrentUser.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(getCurrentUser.fulfilled, (state, { payload }) => {
        console.log('✅ getCurrentUser.fulfilled - New user data:', payload)
        console.log('📧 PayPal email in new data:', payload.paypal_email)
        state.isLoading = false
        state.isLoggedIn = true
        state.user = payload
      })
      .addCase(getCurrentUser.rejected, (state, action) => {
        state.isLoading = false
        if (action.payload) {
          state.error = action.payload.errors
        } else {
          state.error = action.error.message
        }
      })
  },
})

export const { reducer, actions } = authSlice
export const {
  setUserLocation,
  updateTeamId,
  messageCleanUp,
  userWithNameCleanUp,
  updateUserWithName,
  removeUserWithNameNews,
  updateUserWithNameNews,
} = authSlice.actions